import { Box, HStack, Icon, InfoBadge, Text } from '@medely/ui-kit';
import React from 'react';
import { JobDetailsListItem } from '../../JobDetailsListItem';
import { HelpCenterLink } from '@mp/components/helpCenterLink';
import { COVID_REQUIREMENTS, gateNames } from '@mp/constants';
import { DisplayHelpers } from '@mp/utils';
import { BookedGeneralInfo } from './BookedGeneralInfo';
import { AvailableGeneralInfo } from './AvailableGeneralInfo';
import { useCurrentUser } from '@mp/hooks';
import { useGate } from '@mp/hooks/useGate';
import { PostingFieldsGroup } from '@mp/components/postingFieldsGroup/PostingFieldsGroup';
import { JobGeneralInfoProps } from './interface';
import { parseFragmentData } from '@mp/graphql/generated';
import { JobDetailsFragment } from '../graphql';

export const JobGeneralInfo = ({
  job: inputJob,
  smallIcon,
  bookedOrInProgressOrCompleted,
}: JobGeneralInfoProps) => {
  const showSpecialtySpeedbumExtension = useGate(gateNames.specialty_speedbump_extension);
  const job = parseFragmentData(JobDetailsFragment, inputJob);
  const { isSaas, isAgency, currentUser } = useCurrentUser();
  const covidRequirement =
    job?.location?.covid_requirement || job?.location?.state?.covid_requirement;
  const covidText =
    COVID_REQUIREMENTS.info[covidRequirement as keyof typeof COVID_REQUIREMENTS.info] ?? '';

  const isHighRiskPosition = showSpecialtySpeedbumExtension
    ? // @ts-ignore
      (job?.missing_high_risk_specialties?.length ?? 0) > 0 && !bookedOrInProgressOrCompleted
    : job?.positions?.some((p) => p.high_risk) && !bookedOrInProgressOrCompleted;

  const isNewFacilityGuideFormat = useGate(gateNames.facilityGuide);
  const bookedOrInProgress = ['booked', 'in_progress', 'completed'].includes(job?.status ?? '');

  return (
    <>
      {bookedOrInProgressOrCompleted ? (
        <BookedGeneralInfo
          job={inputJob}
          isSaas={isSaas}
          coordinates={currentUser?.coordinates}
          bookedOrInProgressOrCompleted={bookedOrInProgressOrCompleted}
          smallIcon={smallIcon}
        />
      ) : (
        <AvailableGeneralInfo
          job={inputJob}
          isSaas={isSaas}
          coordinates={currentUser?.coordinates}
          isAgency={isAgency}
          bookedOrInProgressOrCompleted={bookedOrInProgressOrCompleted}
          smallIcon={smallIcon}
        />
      )}
      {job?.specialties && (
        <Box py={1}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-star-of-life"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Specialties"
            showRequiredBadge={isHighRiskPosition}
          >
            <HStack gap={0.5} flexWrap="wrap" pt={1}>
              {job.specialties.map((specialty) => (
                <InfoBadge key={specialty?.id} label={specialty?.label || ''} />
              ))}
            </HStack>
          </JobDetailsListItem>
        </Box>
      )}
      <Box py={1}>
        <JobDetailsListItem
          icon={
            <Icon
              name="light-head-side-medical"
              iconSize={smallIcon ? '14px' : '20px'}
              color="text.primary"
              variant="custom"
            />
          }
          title={isNewFacilityGuideFormat ? 'Patients' : 'Patient population'}
        >
          <Text size="m">{DisplayHelpers.job.patientPopulation(job?.patient_population)}</Text>
          {isNewFacilityGuideFormat && (
            <PostingFieldsGroup postingFieldContents={job?.posting_field_contents} />
          )}
        </JobDetailsListItem>
      </Box>
      {job?.clinical_contact && bookedOrInProgress && isNewFacilityGuideFormat && (
        <Box py={1}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-user-doctor"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Clinical contact"
          >
            <Text size="m">
              {job.clinical_contact?.first_name || ''} {job.clinical_contact?.last_name || ''}
            </Text>
          </JobDetailsListItem>
        </Box>
      )}
      {isNewFacilityGuideFormat && (
        <Box py={1}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-tag"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title={`Job ID: ${job?.id}`}
          >
            {job?.detail && (
              <Text size="m" color="text.primary" testId="job-details-description">
                {job.detail}
              </Text>
            )}
          </JobDetailsListItem>
        </Box>
      )}
      {covidRequirement && (
        <Box mb={3} testId="job-highlights-covid-requirement">
          <JobDetailsListItem
            icon={
              <Icon
                name="light-syringe"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Covid protocol"
          >
            <>
              <Text size="m">{covidText}</Text>
              <Box testId="job-highlights-covid-requirement-link">
                <HelpCenterLink text="Learn more" configKey="covidVaccineOrTest" />
              </Box>
            </>
          </JobDetailsListItem>
        </Box>
      )}
    </>
  );
};
